import React, { useEffect, useState } from 'react';
import { Building2, ChevronDown, Check, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

// Shared helper: clear practice context (used by topbar back button + the
// "Plattform-Übersicht" entry in the superadmin switcher).
export function clearPracticeContext() {
  delete api.defaults.headers['X-Practice-Id'];
  localStorage.removeItem('studioops_active_practice');
  window.location.assign('/einstellungen');
}

export default function LocationSwitcher({ variant = 'topbar' }) {
  const { practice, user } = useAuth();
  const { t } = useTranslation();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get('/api/locations').then((r) => setList(r.data.locations || [])).catch(() => {});
  }, [user]);

  // Sort: Hauptstandort first, then branches by name.
  const sorted = React.useMemo(() => {
    return [...list].sort((a, b) => {
      if (a.is_main_location && !b.is_main_location) return -1;
      if (!a.is_main_location && b.is_main_location) return 1;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [list]);

  const activeId = practice?._id || localStorage.getItem('studioops_active_practice') || null;
  const active = sorted.find((l) => l._id === activeId);
  const isSuperadmin = user?.role === 'superadmin';

  if (variant === 'mobile-label') {
    if (!active) return isSuperadmin ? (
      <span className="text-xs text-text-secondary truncate max-w-[180px]" data-testid="mobile-location-label">
        <Globe size={11} className="inline mr-1 -mt-0.5"/>{t('topbar.platformOverview')}
      </span>
    ) : null;
    return (
      <span className="text-xs text-text-secondary truncate max-w-[180px]" data-testid="mobile-location-label">
        <Building2 size={11} className="inline mr-1 -mt-0.5"/>
        {active.location_label || active.name}
      </span>
    );
  }

  // Hide switcher entirely if only one item AND not superadmin (no need to
  // toggle between platform-overview and one practice).
  if (!sorted.length || (sorted.length < 2 && !isSuperadmin)) {
    return variant === 'mobile' ? null : null;
  }

  async function switchTo(loc) {
    api.defaults.headers['X-Practice-Id'] = loc._id;
    localStorage.setItem('studioops_active_practice', loc._id);
    setOpen(false);
    window.location.reload();
  }

  function PlatformEntry({ mobile }) {
    if (!isSuperadmin) return null;
    const isActive = !active;
    if (mobile) {
      return (
        <button
          onClick={clearPracticeContext}
          className={`w-full flex items-center justify-between gap-2 rounded-md px-3 text-left text-sm transition-colors ${
            isActive ? 'bg-white text-navy font-semibold shadow-sm' : 'bg-white/5 text-white hover:bg-white/10'
          }`}
          style={{ minHeight: 48 }}
          data-testid="mobile-platform-overview"
        >
          <div className="flex items-center gap-2"><Globe size={14}/><span>{t('topbar.platformOverview')}</span></div>
          {isActive && <Check size={14}/>}
        </button>
      );
    }
    return (
      <button
        onClick={clearPracticeContext}
        className={`w-full text-left rounded px-3 py-2 hover:bg-background text-sm flex items-center justify-between ${isActive ? 'font-semibold text-accent' : ''}`}
        data-testid="platform-overview-option"
      >
        <div className="flex items-center gap-2"><Globe size={14}/><span>{t('topbar.platformOverview')}</span></div>
        {isActive && <Check size={14}/>}
      </button>
    );
  }

  if (variant === 'mobile') {
    return (
      <div data-testid="mobile-location-switcher" className="px-1">
        <p className="text-[10px] uppercase tracking-wider text-white/40 px-2 mb-2 font-semibold">{t('topbar.switchLocation')}</p>
        <div className="space-y-1.5">
          <PlatformEntry mobile />
          {sorted.map((l) => {
            const isActive = l._id === activeId;
            return (
              <button
                key={l._id}
                onClick={() => switchTo(l)}
                className={`w-full flex items-center justify-between gap-2 rounded-md px-3 text-left text-sm transition-colors ${
                  isActive ? 'bg-white text-navy font-semibold shadow-sm' : 'bg-white/5 text-white hover:bg-white/10'
                }`}
                style={{ minHeight: 48 }}
                data-testid={`mobile-location-option-${l.slug}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Building2 size={14} strokeWidth={isActive ? 2.2 : 1.8} className="shrink-0"/>
                  <div className="min-w-0">
                    <div className="truncate flex items-center gap-1.5">
                      {l.location_label || l.name}
                      {l.is_main_location && (
                        <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-semibold ${isActive ? 'bg-accent/10 text-accent' : 'bg-white/10 text-white/80'}`}>★ {t('settings.locationMgmt.main')}</span>
                      )}
                    </div>
                    {l.twilio_whatsapp_number && (
                      <div className={`text-[10px] font-mono truncate ${isActive ? 'text-text-secondary' : 'text-white/50'}`}>{l.twilio_whatsapp_number}</div>
                    )}
                  </div>
                </div>
                {isActive && <Check size={14} className="shrink-0"/>}
              </button>
            );
          })}
        </div>
        <div className="mt-3 h-px bg-white/10"/>
      </div>
    );
  }

  // Desktop topbar variant
  return (
    <div className="relative" data-testid="location-switcher">
      <button onClick={() => setOpen((o) => !o)} className="so-btn so-btn-secondary text-xs" data-testid="location-switcher-btn">
        {active ? <Building2 size={14}/> : <Globe size={14}/>}
        <span className="hidden sm:inline truncate max-w-[160px]">
          {active?.location_label || (isSuperadmin ? t('topbar.platformOverview') : t('topbar.locationLabel'))}
        </span>
        <ChevronDown size={12}/>
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-72 z-30 so-card p-1 shadow-lg" data-testid="location-switcher-menu">
          <PlatformEntry/>
          {isSuperadmin && sorted.length > 0 && <div className="my-1 h-px bg-border"/>}
          {sorted.map((l) => (
            <button
              key={l._id}
              onClick={() => switchTo(l)}
              className={`w-full text-left rounded px-3 py-2 hover:bg-background text-sm flex items-center justify-between ${l._id === activeId ? 'font-semibold text-accent' : ''}`}
              data-testid={`location-option-${l.slug}`}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 truncate">
                  {l.location_label || l.name}
                  {l.is_main_location && (
                    <span className="text-[9px] uppercase tracking-wider bg-accent/10 text-accent px-1.5 py-0.5 rounded-full font-semibold shrink-0">★ {t('settings.locationMgmt.main')}</span>
                  )}
                </div>
                <div className="text-[11px] text-text-secondary font-mono">{l.twilio_whatsapp_number || ''}</div>
              </div>
              {l._id === activeId && <Check size={14}/>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
